/**
 * CockpitDiretoriaPage — Painel 360° de Governança Executiva
 *
 * Cockpit unificado para Diretor Industrial / CEO:
 *   Ribbon Executivo com KPIs Hoshin + Andon
 *   Grid Matricial de Unidades de Valor / Departamentos
 *     └─ Gestor & Time (FTE/capacidade)
 *     └─ Engenharia de Fluxo (Mapas VSM ativos)
 *     └─ Projetos Kaizen (progresso)
 *   Bottom Sheet — Central de Ações com semáforo de SLA
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Target,
  Layers,
  Activity,
  AlertTriangle,
  Users,
  ChevronDown,
  ChevronRight,
  Clock,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Minus,
  MapPin,
  Cpu,
  ChevronsUp,
  PanelBottomOpen,
  X,
} from 'lucide-react';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAndonAlerts } from '../hooks/useAndonAlerts';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import type { ActionSlaStatus, EnterpriseAction } from '../services/actionService';
import type { OrgMember, OrgDepartment } from '../services/organizationService';

// ─── Fallback sectors (shown ONLY when org has no configured departments) ───────

const FALLBACK_SECTORS = [
  'Excelência Operacional',
  'Operações / Fábrica',
  'Logística e Supply Chain',
  'Qualidade / Laboratório',
] as const;

const FALLBACK_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'] as const;

/** Generate a deterministic color from a string (used when dept has no color field) */
function hashColor(str: string): string {
  const palette = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899',
    '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6',
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

// ─── Styling constants (mirrors OperationsCenterPage conventions) ────────────

const PANEL =
  'rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] overflow-hidden shadow-[0_1px_4px_rgba(0,0,0,0.06)]';
const PANEL_HEADER =
  'flex items-center justify-between px-3 py-2 border-b border-[var(--border-default)] bg-[var(--bg-elevated)]';
const PANEL_TITLE =
  'text-[10px] font-semibold uppercase tracking-widest text-[var(--text-secondary)]';
const ROW_BASE =
  'flex items-center gap-2 px-3 py-2 border-b border-[var(--border-default)] last:border-b-0 text-[11px] text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] transition-colors';

// ─── Internal Types ──────────────────────────────────────────

interface DeptMap {
  id: string;
  title: string;
  map_type: 'industrial' | 'office';
  plt_minutes?: number | null;
  vat_minutes?: number | null;
  tp_minutes?: number | null;
  tra_minutes?: number | null;
  status: string;
}

interface DeptProject {
  id: string;
  name: string;
  status: string;
  progress: number;
}

interface DeptUnit {
  department: OrgDepartment;
  manager: OrgMember | null;
  members: OrgMember[];
  maps: DeptMap[];
  projects: DeptProject[];
  /** Total active (non-cancelled) actions for this dept */
  actionCount: number;
  /** Actions past due_date and not completed/cancelled */
  overdueCount: number;
  /** True when generated from static fallback — no real DB entry */
  isSynthetic: boolean;
}

interface DeptQueryResult {
  units: DeptUnit[];
  isFallback: boolean;
}

interface CockpitAction extends EnterpriseAction {
  department_id?: string | null;
}

// ─── Data Fetching ────────────────────────────────────────────

/**
 * Three-step dept resolution (Poka-Yoke) + cascade aggregation:
 *   1. org_departments table  → real configured departments
 *   2. user_organizations     → infer from member dept assignments
 *   3. static fallback array  → never show a blank grid
 *
 * Cascade logic for actions (no department_id FK in actions table):
 *   action.assigned_to → user_organizations.department_id → dept
 *   action.project_id  → project_members.user_id → user_organizations.department_id → dept
 *
 * Projects per dept:
 *   project_members.user_id → user_organizations.department_id → dept
 */
async function fetchDeptUnitsWithFallback(orgId: string): Promise<DeptQueryResult> {
  const today = new Date().toISOString().split('T')[0];

  // Round-trip 1: departments + members (with dept assignment) in parallel
  const [deptRes, memberRes] = await Promise.all([
    supabase
      .from('org_departments')
      .select('*')
      .eq('organization_id', orgId)
      .order('name'),
    // select('*') ensures department_id is included even if not in generated types
    supabase
      .from('user_organizations')
      .select('*, profiles(full_name, email)')
      .eq('organization_id', orgId),
  ]);

  let departments: OrgDepartment[] = (deptRes.data ?? []) as OrgDepartment[];
  const rawMembers = (memberRes.data ?? []) as Record<string, unknown>[];

  // ── Build userDeptMap: user_id → department_id ───────────────────────────────
  const userDeptMap = new Map<string, string>();
  for (const m of rawMembers) {
    const uid = m.user_id as string | null;
    const did = m.department_id as string | null;
    if (uid && did) userDeptMap.set(uid, did);
  }

  // ── Step 2: derive departments from member dept assignments ──────────────────
  if (departments.length === 0) {
    const assignedDeptIds = [...new Set([...userDeptMap.values()])];
    if (assignedDeptIds.length > 0) {
      const { data: found } = await supabase
        .from('org_departments')
        .select('*')
        .in('id', assignedDeptIds);
      departments = (found ?? []) as OrgDepartment[];
    }
  }

  // ── Step 3: static fallback ──────────────────────────────────────────────────
  let isFallback = false;
  if (departments.length === 0) {
    isFallback = true;
    departments = FALLBACK_SECTORS.map((name, i) => ({
      id: `synthetic-${i}`,
      organization_id: orgId,
      name,
      color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      parent_department_id: null,
      created_at: new Date().toISOString(),
    }));
  }

  // ── Ensure every dept has a display color ────────────────────────────────────
  departments = departments.map((d) => ({
    ...d,
    color: (d as OrgDepartment & { color?: string }).color || hashColor(d.name),
  })) as OrgDepartment[];

  // ── Build member list ────────────────────────────────────────────────────────
  const members: OrgMember[] = rawMembers.map((m) => {
    const profile = m.profiles as { full_name?: string; email?: string } | null;
    return {
      user_id: m.user_id as string,
      user_name: profile?.full_name ?? (m.user_id as string),
      user_email: profile?.email ?? '',
      role: null,
      role_type: (m.role_type as OrgMember['role_type']) ?? 'operational',
      role_id: (m.role_id as string | null) ?? null,
      role_name: null,
      department_id: (m.department_id as string | null) ?? null,
      department_name: null,
      reports_to_user_id: (m.reports_to_user_id as string | null) ?? null,
      reports_to_name: null,
      subordinate_count: 0,
      active_task_count: 0,
      late_task_count: 0,
    };
  });

  if (isFallback) {
    // ── Fallback: no dept structure — count everything in first bucket ──────────
    const totalActions = (
      await supabase
        .from('actions')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .neq('status', 'cancelled')
    ).count ?? 0;

    const overdueTotal = (
      await supabase
        .from('actions')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .not('status', 'in', '(completed,cancelled)')
        .lt('due_date', today)
    ).count ?? 0;

    const units: DeptUnit[] = departments.map((dept, idx) => ({
      department: dept,
      manager: null,
      members: idx === 0 ? members.filter((m) => !m.department_id) : [],
      maps: [],
      projects: [],
      actionCount: idx === 0 ? totalActions : 0,
      overdueCount: idx === 0 ? overdueTotal : 0,
      isSynthetic: true,
    }));
    return { units, isFallback: true };
  }

  // ── Round-trip 2: actions + project_members + projects (parallel) ────────────
  const [actionRes, pmRes, projRes] = await Promise.all([
    supabase
      .from('actions')
      .select('id, assigned_to, project_id, due_date, status')
      .eq('organization_id', orgId)
      .neq('status', 'cancelled'),
    supabase
      .from('project_members')
      .select('project_id, user_id'),
    supabase
      .from('projects')
      .select('id, nome_projeto, visibility')
      .eq('organization_id', orgId)
      .neq('visibility', 'archived'),
  ]);

  type RawAction = { id: string; assigned_to: string | null; project_id: string | null; due_date: string | null; status: string | null };
  type RawPM = { project_id: string; user_id: string };
  type RawProject = { id: string; nome_projeto: string; visibility: string | null };

  const rawActions = (actionRes.data ?? []) as RawAction[];
  const rawPMs = (pmRes.data ?? []) as RawPM[];
  const rawProjects = (projRes.data ?? []) as RawProject[];

  // ── project → Set<dept_id> via project_members → userDeptMap ────────────────
  const projectDeptMap = new Map<string, Set<string>>();
  for (const pm of rawPMs) {
    const deptId = userDeptMap.get(pm.user_id);
    if (!deptId) continue;
    if (!projectDeptMap.has(pm.project_id)) projectDeptMap.set(pm.project_id, new Set());
    projectDeptMap.get(pm.project_id)!.add(deptId);
  }

  // ── Aggregate: actionCount + overdueCount per dept ───────────────────────────
  const actionCountByDept = new Map<string, number>();
  const overdueCountByDept = new Map<string, number>();

  function addToDept(deptId: string, isOverdue: boolean) {
    actionCountByDept.set(deptId, (actionCountByDept.get(deptId) ?? 0) + 1);
    if (isOverdue) overdueCountByDept.set(deptId, (overdueCountByDept.get(deptId) ?? 0) + 1);
  }

  const deptIdSet = new Set(departments.map((d) => d.id));

  for (const action of rawActions) {
    const isOverdue =
      Boolean(action.due_date) &&
      action.due_date! < today &&
      action.status !== 'completed';

    const assignedDept = action.assigned_to ? userDeptMap.get(action.assigned_to) : undefined;

    if (assignedDept && deptIdSet.has(assignedDept)) {
      // Primary: action assigned to a member of this dept
      addToDept(assignedDept, isOverdue);
    } else if (action.project_id) {
      // Cascade: action belongs to a project whose members span dept(s)
      const projectDepts = projectDeptMap.get(action.project_id);
      if (projectDepts) {
        for (const dId of projectDepts) {
          if (deptIdSet.has(dId)) addToDept(dId, isOverdue);
        }
      }
    }
  }

  // ── Aggregate: projects per dept ─────────────────────────────────────────────
  const projectsByDept = new Map<string, DeptProject[]>();
  for (const proj of rawProjects) {
    const deptIds = projectDeptMap.get(proj.id);
    if (!deptIds) continue;
    for (const dId of deptIds) {
      if (!deptIdSet.has(dId)) continue;
      if (!projectsByDept.has(dId)) projectsByDept.set(dId, []);
      projectsByDept.get(dId)!.push({
        id: proj.id,
        name: proj.nome_projeto,
        status: proj.visibility ?? 'published',
        progress: 0,
      });
    }
  }

  // ── Build units ──────────────────────────────────────────────────────────────
  const units: DeptUnit[] = departments.map((dept) => {
    const deptMembers = members.filter((m) => m.department_id === dept.id);
    const manager =
      deptMembers.find((m) => m.role_type === 'manager' || m.role_type === 'director') ?? null;

    return {
      department: dept,
      manager,
      members: deptMembers,
      maps: [],
      projects: projectsByDept.get(dept.id) ?? [],
      actionCount: actionCountByDept.get(dept.id) ?? 0,
      overdueCount: overdueCountByDept.get(dept.id) ?? 0,
      isSynthetic: false,
    };
  });

  return { units, isFallback: false };
}

// Maps and projects are now resolved inside fetchDeptUnitsWithFallback via cascade.
// These stubs are kept to avoid breaking the useQuery keys during transition.
async function fetchMapsForDepts(
  _orgId: string,
  _deptIds: string[],
): Promise<Map<string, DeptMap[]>> {
  return new Map();
}

async function fetchActionsForDept(
  orgId: string,
  deptId: string,
  isSynthetic: boolean,
  _deptName?: string,
): Promise<CockpitAction[]> {
  if (isSynthetic) {
    // Fallback: return all org actions
    const { data } = await supabase
      .from('actions')
      .select('*')
      .eq('organization_id', orgId)
      .neq('status', 'cancelled')
      .order('due_date', { ascending: true })
      .limit(50);
    return (data ?? []) as CockpitAction[];
  }

  // 1. Get user_ids that belong to this dept
  const { data: deptMembers } = await supabase
    .from('user_organizations')
    .select('user_id')
    .eq('organization_id', orgId)
    .eq('department_id', deptId);

  const memberIds = ((deptMembers ?? []) as { user_id: string }[]).map((m) => m.user_id);

  // 2. Get project_ids where at least one member of this dept is a project_member
  let projectIds: string[] = [];
  if (memberIds.length > 0) {
    const { data: pmRows } = await supabase
      .from('project_members')
      .select('project_id')
      .in('user_id', memberIds);
    projectIds = [...new Set(((pmRows ?? []) as { project_id: string }[]).map((r) => r.project_id))];
  }

  // 3. Fetch actions matching by assigned_to OR project_id
  const orFilters: string[] = [];
  if (memberIds.length > 0) orFilters.push(`assigned_to.in.(${memberIds.join(',')})`);
  if (projectIds.length > 0) orFilters.push(`project_id.in.(${projectIds.join(',')})`);

  if (orFilters.length === 0) return [];

  const { data } = await supabase
    .from('actions')
    .select('*')
    .eq('organization_id', orgId)
    .neq('status', 'cancelled')
    .or(orFilters.join(','))
    .order('due_date', { ascending: true })
    .limit(50);

  return (data ?? []) as CockpitAction[];
}

async function fetchHoshinCount(orgId: string): Promise<number> {
  // hoshin_objectives table — uses select with count; table may not exist in all envs
  const { count, error } = await supabase
    .from('hoshin_objectives')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('status', 'active');
  if (error) return 0;
  return count ?? 0;
}

async function fetchProjectsOnTimeCount(orgId: string): Promise<number> {
  const today = new Date().toISOString().split('T')[0];
  // Use `projects` table with `expected_end_date` (schema-accurate field name)
  const { count, error } = await supabase
    .from('projects')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .gte('expected_end_date', today);
  if (error) return 0;
  return count ?? 0;
}

// ─── Sub-components ───────────────────────────────────────────

interface InitialsAvatarProps {
  name: string;
  size?: string;
}

function InitialsAvatar({ name, size = 'w-6 h-6 text-[10px]' }: InitialsAvatarProps) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      className={`${size} rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center flex-shrink-0 select-none`}
      title={name}
    >
      {initials}
    </span>
  );
}

interface SlaChipProps {
  status: ActionSlaStatus | null | undefined;
}

function SlaChip({ status }: SlaChipProps) {
  if (!status || status === 'on_time') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
        <CheckCircle2 size={10} />
        No prazo
      </span>
    );
  }
  if (status === 'andon_triggered') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20">
        <AlertTriangle size={10} />
        Andon
      </span>
    );
  }
  if (status === 'critical_director') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold bg-red-500/10 text-red-600 border border-red-500/20 animate-pulse">
        <ChevronsUp size={10} />
        Crítico
      </span>
    );
  }
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold bg-red-500/10 text-red-500 border border-red-500/20">
        <XCircle size={10} />
        Atrasado
      </span>
    );
  }
  return null;
}

interface ProgressBarProps {
  value: number; // 0-100
}

function ProgressBar({ value }: ProgressBarProps) {
  const clamped = Math.min(100, Math.max(0, value));
  return (
    <div className="w-full h-1.5 rounded-full bg-[var(--bg-surface-3)] overflow-hidden">
      <div
        className="h-full rounded-full bg-emerald-500 transition-[width] duration-300"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

interface MapBadgeProps {
  map: DeptMap;
}

function MapBadge({ map }: MapBadgeProps) {
  const isIndustrial = map.map_type === 'industrial';
  const metric1 = isIndustrial
    ? map.plt_minutes != null
      ? `PLT ${map.plt_minutes}min`
      : null
    : map.tp_minutes != null
      ? `TP ${map.tp_minutes}min`
      : null;
  const metric2 = isIndustrial
    ? map.vat_minutes != null
      ? `VAT ${map.vat_minutes}min`
      : null
    : map.tra_minutes != null
      ? `TRA ${map.tra_minutes}min`
      : null;

  return (
    <span
      title={[metric1, metric2].filter(Boolean).join(' · ') || map.title}
      className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium border border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] cursor-default hover:bg-[var(--bg-surface-3)] transition-colors select-none"
    >
      <MapPin size={9} className="flex-shrink-0 text-blue-500" />
      <span className="max-w-[80px] truncate">{map.title}</span>
      {metric1 && (
        <span className="text-[9px] text-[var(--text-muted)] hidden sm:inline">
          {metric1}
        </span>
      )}
    </span>
  );
}

// ─── Department Row (expandable) ─────────────────────────────

interface DeptRowProps {
  unit: DeptUnit;
  orgId: string;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpand: (deptId: string) => void;
  onSelect: (deptId: string) => void;
}

function DeptRow({
  unit,
  orgId,
  isSelected,
  isExpanded,
  onToggleExpand,
  onSelect,
}: DeptRowProps) {
  const { department, manager, members, maps, projects } = unit;
  const deptColor = department.color ?? '#3b82f6';

  return (
    <>
      {/* ── Header row ── */}
      <tr
        className={`cursor-pointer transition-colors ${
          isSelected ? 'bg-blue-500/5' : 'hover:bg-[var(--bg-elevated)]'
        }`}
        onClick={() => {
          onToggleExpand(department.id);
          onSelect(department.id);
        }}
      >
        {/* Expand toggle */}
        <td className="px-2 py-2 w-6">
          <button
            className="p-0.5 rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            aria-label={isExpanded ? 'Recolher' : 'Expandir'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(department.id);
            }}
          >
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        </td>

        {/* Dept color dot + name */}
        <td className="px-2 py-2 min-w-[140px]">
          <div className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: deptColor }}
            />
            <span className="text-[11px] font-medium text-[var(--text-primary)] truncate max-w-[120px]">
              {department.name}
            </span>
          </div>
        </td>

        {/* Manager */}
        <td className="px-2 py-2 min-w-[120px]">
          {manager ? (
            <div className="flex items-center gap-1.5">
              <InitialsAvatar name={manager.user_name} />
              <span className="text-[11px] text-[var(--text-secondary)] truncate max-w-[90px]">
                {manager.user_name}
              </span>
            </div>
          ) : (
            <span className="text-[10px] text-[var(--text-muted)]">—</span>
          )}
        </td>

        {/* Team size */}
        <td className="px-2 py-2 w-16 text-center">
          <span className="inline-flex items-center gap-1 text-[10px] text-[var(--text-secondary)]">
            <Users size={10} />
            {members.length}
          </span>
        </td>

        {/* Maps count — shows projects linked via member cascade */}
        <td className="px-2 py-2 w-16 text-center">
          <span className="inline-flex items-center gap-1 text-[10px] text-[var(--text-secondary)]">
            <Layers size={10} />
            {projects.length > 0 ? projects.length : <span className="text-[var(--text-muted)]">—</span>}
          </span>
        </td>

        {/* Projects count */}
        <td className="px-2 py-2 w-16 text-center">
          <span className="inline-flex items-center gap-1 text-[10px] text-[var(--text-secondary)]">
            <Target size={10} />
            {projects.length > 0 ? projects.length : <span className="text-[var(--text-muted)]">—</span>}
          </span>
        </td>

        {/* FTE load indicator */}
        <td className="px-2 py-2 w-24">
          <FteIndicator members={members} />
        </td>

        {/* Action count — composite: total + overdue */}
        <td className="px-2 py-2 w-24 text-center">
          {unit.actionCount > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 flex-wrap justify-center">
              <Activity size={10} />
              <span>{unit.actionCount}</span>
              {unit.overdueCount > 0 && (
                <span className="text-red-500 font-medium">
                  ({unit.overdueCount} atras.)
                </span>
              )}
            </span>
          ) : (
            <span className="text-[10px] text-[var(--text-muted)]">—</span>
          )}
        </td>
      </tr>

      {/* ── Expanded detail rows ── */}
      {isExpanded && (
        <tr>
          <td colSpan={8} className="p-0 bg-[var(--bg-elevated)]">
            <div className="grid grid-cols-3 gap-px bg-[var(--border-default)] border-b border-[var(--border-default)]">
              {/* A) Gestor & Time */}
              <div className="bg-[var(--bg-surface)] px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest font-semibold text-[var(--text-muted)] mb-2 flex items-center gap-1">
                  <Users size={9} />
                  Gestor &amp; Time
                </p>
                <div className="flex flex-col gap-1.5">
                  {unit.isSynthetic && (
                    <span className="text-[9px] text-amber-600 font-medium block mb-1">
                      Setor sem cadastro — exibindo membros sem setor vinculado
                    </span>
                  )}
                  {members.length === 0 && (
                    <span className="text-[10px] text-[var(--text-muted)]">Sem membros vinculados</span>
                  )}
                  {members.map((m) => (
                    <div key={m.user_id} className="flex items-center gap-1.5">
                      <InitialsAvatar name={m.user_name} size="w-5 h-5 text-[9px]" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-[10px] font-medium text-[var(--text-primary)] truncate">
                          {m.user_name}
                        </span>
                        <span className="text-[9px] text-[var(--text-muted)] capitalize">
                          {m.role_type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* B) Engenharia de Fluxo */}
              <div className="bg-[var(--bg-surface)] px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest font-semibold text-[var(--text-muted)] mb-2 flex items-center gap-1">
                  <Layers size={9} />
                  Engenharia de Fluxo
                </p>
                {maps.length === 0 ? (
                  <span className="text-[10px] text-[var(--text-muted)]">Nenhum mapa ativo</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {maps.map((map) => (
                      <MapBadge key={map.id} map={map} />
                    ))}
                  </div>
                )}
              </div>

              {/* C) Projetos Kaizen */}
              <div className="bg-[var(--bg-surface)] px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest font-semibold text-[var(--text-muted)] mb-2 flex items-center gap-1">
                  <Target size={9} />
                  Projetos
                </p>
                {projects.length === 0 ? (
                  <span className="text-[10px] text-[var(--text-muted)]">Nenhum projeto ativo</span>
                ) : (
                  <div className="flex flex-col gap-2">
                    {projects.map((p) => (
                      <div key={p.id} className="flex flex-col gap-0.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-medium text-[var(--text-primary)] truncate">
                            {p.name}
                          </span>
                          <span className="text-[9px] text-[var(--text-muted)] flex-shrink-0">
                            {p.progress}%
                          </span>
                        </div>
                        <ProgressBar value={p.progress} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── FTE Load Indicator ───────────────────────────────────────

interface FteIndicatorProps {
  members: OrgMember[];
}

function FteIndicator({ members }: FteIndicatorProps) {
  const count = members.length;
  if (count === 0) return <span className="text-[10px] text-[var(--text-muted)]">—</span>;
  // Simple heuristic: > 8 members = potential overload
  const level = count > 8 ? 'overload' : count > 5 ? 'high' : 'normal';
  const colorMap = { normal: 'bg-emerald-500', high: 'bg-amber-500', overload: 'bg-red-500' };
  const textMap = { normal: 'text-emerald-600', high: 'text-amber-600', overload: 'text-red-600' };
  return (
    <div className="flex items-center gap-1">
      <span className={`w-1.5 h-1.5 rounded-full ${colorMap[level]} flex-shrink-0`} />
      <span className={`text-[10px] font-medium ${textMap[level]}`}>
        {count} FTE
      </span>
    </div>
  );
}

// ─── Bottom Sheet — Central de Ações ─────────────────────────

interface ActionSheetProps {
  orgId: string;
  deptId: string | null;
  deptName: string;
  isSynthetic: boolean;
  onClose: () => void;
}

function ActionSheet({ orgId, deptId, deptName, isSynthetic, onClose }: ActionSheetProps) {
  const { data: actions = [], isLoading } = useQuery<CockpitAction[]>({
    queryKey: ['cockpit-actions', orgId, deptId, isSynthetic, deptName],
    queryFn: () =>
      deptId
        ? fetchActionsForDept(orgId, deptId, isSynthetic, deptName)
        : Promise.resolve([]),
    enabled: Boolean(orgId && deptId),
    staleTime: 30_000,
  });

  const today = new Date();

  function deadlineColor(due: string | null): string {
    if (!due) return 'text-[var(--text-muted)]';
    const d = new Date(due);
    const diffDays = Math.ceil((d.getTime() - today.getTime()) / 86400000);
    if (diffDays < 0) return 'text-red-600 font-semibold';
    if (diffDays <= 2) return 'text-amber-600 font-semibold';
    return 'text-emerald-600';
  }

  function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }

  return (
    <div className="border-t border-[var(--border-default)] bg-[var(--bg-surface)] flex flex-col" style={{ height: 260 }}>
      {/* Sheet header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-default)] bg-[var(--bg-elevated)] flex-shrink-0">
        <div className="flex items-center gap-2">
          <Activity size={12} className="text-blue-500" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-secondary)]">
            Central de Ações
          </span>
          <span className="text-[10px] text-[var(--text-muted)]">—</span>
          <span className="text-[10px] font-medium text-[var(--text-primary)]">{deptName}</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-3)] transition-colors"
          aria-label="Fechar"
        >
          <X size={12} />
        </button>
      </div>

      {/* Action list */}
      <div className="overflow-auto flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-[11px] text-[var(--text-muted)] animate-pulse">Carregando ações…</span>
          </div>
        ) : actions.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-[11px] text-[var(--text-muted)]">Nenhuma ação ativa neste setor.</span>
          </div>
        ) : (
          <table className="w-full border-collapse text-[11px]">
            <thead className="sticky top-0 bg-[var(--bg-elevated)] z-10">
              <tr>
                <th className="px-3 py-1.5 text-left text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-semibold w-[260px]">Ação</th>
                <th className="px-3 py-1.5 text-left text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">Responsável</th>
                <th className="px-3 py-1.5 text-center text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-semibold w-20">Esforço</th>
                <th className="px-3 py-1.5 text-center text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-semibold w-20">Custo</th>
                <th className="px-3 py-1.5 text-center text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-semibold w-20">Prazo</th>
                <th className="px-3 py-1.5 text-center text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-semibold w-24">SLA</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((action) => (
                <tr key={action.id} className={ROW_BASE + ' table-row'}>
                  <td className="px-3 py-2">
                    <span className="font-medium text-[var(--text-primary)] line-clamp-1 max-w-[250px] block">{action.title}</span>
                  </td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">
                    {action.assigned_to ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-center text-[var(--text-secondary)]">
                    {action.effort != null ? (
                      <span className="inline-flex items-center gap-0.5">
                        <Cpu size={9} className="text-blue-400" />
                        {action.effort}
                      </span>
                    ) : (
                      <Minus size={10} className="mx-auto text-[var(--text-muted)]" />
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-[var(--text-secondary)]">
                    {action.impact != null ? (
                      <span className="inline-flex items-center gap-0.5">
                        <TrendingUp size={9} className="text-emerald-500" />
                        {action.impact}
                      </span>
                    ) : (
                      <Minus size={10} className="mx-auto text-[var(--text-muted)]" />
                    )}
                  </td>
                  <td className={`px-3 py-2 text-center ${deadlineColor(action.due_date)}`}>
                    <span className="inline-flex items-center gap-0.5">
                      <Clock size={9} />
                      {formatDate(action.due_date)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <SlaChip status={action.sla_status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Executive Ribbon ─────────────────────────────────────────

interface ExecRibbonProps {
  hoshinCount: number;
  projectsOnTime: number;
  andonTriggered: number;
  critical: number;
  isLoadingKpis: boolean;
}

function ExecRibbon({ hoshinCount, projectsOnTime, andonTriggered, critical, isLoadingKpis }: ExecRibbonProps) {
  const hasCritical = critical > 0;

  const kpis = [
    {
      icon: <Target size={14} className="text-blue-500" />,
      label: 'Objetivos Hoshin Ativos',
      value: hoshinCount,
      pulse: false,
      colorClass: 'text-[var(--text-primary)]',
    },
    {
      icon: <CheckCircle2 size={14} className="text-emerald-500" />,
      label: 'Projetos no Prazo',
      value: projectsOnTime,
      pulse: false,
      colorClass: 'text-emerald-600',
    },
    {
      icon: <Activity size={14} className="text-amber-500" />,
      label: 'Andons SLA Disparados',
      value: andonTriggered,
      pulse: false,
      colorClass: andonTriggered > 0 ? 'text-amber-600' : 'text-[var(--text-secondary)]',
    },
    {
      icon: <AlertTriangle size={14} className={hasCritical ? 'text-red-500' : 'text-[var(--text-muted)]'} />,
      label: 'Crítico Diretor',
      value: critical,
      pulse: hasCritical,
      colorClass: hasCritical ? 'text-red-600' : 'text-[var(--text-secondary)]',
    },
  ];

  return (
    <div className="flex items-center gap-1 px-3 py-1.5 border-b border-[var(--border-default)] bg-[var(--bg-elevated)] flex-shrink-0">
      {kpis.map((kpi, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <span className="w-px h-5 bg-[var(--border-default)] mx-1 flex-shrink-0" />
          )}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] select-none ${
              kpi.pulse ? 'animate-pulse border-red-400/40 bg-red-500/5' : ''
            }`}
          >
            {kpi.icon}
            <div className="flex flex-col leading-none">
              <span className="text-[8.5px] uppercase tracking-widest text-[var(--text-muted)] font-semibold whitespace-nowrap">
                {kpi.label}
              </span>
              <span
                className={`text-sm font-bold tabular-nums leading-tight ${kpi.colorClass} ${
                  isLoadingKpis ? 'opacity-40' : ''
                }`}
              >
                {isLoadingKpis ? '…' : kpi.value}
              </span>
            </div>
          </div>
        </React.Fragment>
      ))}

      <span className="ml-auto text-[9px] text-[var(--text-muted)] flex items-center gap-1">
        <Cpu size={9} />
        Cockpit Diretoria
      </span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export const CockpitDiretoriaPage: React.FC = () => {
  const orgId = useOrganizationId();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // ── Andon alerts ──
  const { counts: andonCounts } = useAndonAlerts(orgId || null, userId, 'director');

  // ── KPI queries ──
  const { data: hoshinCount = 0, isLoading: loadingHoshin } = useQuery<number>({
    queryKey: ['cockpit-hoshin-count', orgId],
    queryFn: () => fetchHoshinCount(orgId),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });

  const { data: projectsOnTime = 0, isLoading: loadingProjects } = useQuery<number>({
    queryKey: ['cockpit-projects-ontime', orgId],
    queryFn: () => fetchProjectsOnTimeCount(orgId),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });

  // ── Departments + members + action/project counts (cascade) ──
  const {
    data: deptQueryResult,
    isLoading: loadingUnits,
  } = useQuery<DeptQueryResult>({
    queryKey: ['cockpit-dept-units', orgId],
    queryFn: () => fetchDeptUnitsWithFallback(orgId),
    enabled: Boolean(orgId),
    staleTime: 120_000,
  });

  // Units already include projects, actionCount, overdueCount from the main fetch
  const units = deptQueryResult?.units ?? [];
  const isFallback = deptQueryResult?.isFallback ?? false;

  const selectedUnit = useMemo(
    () => units.find((u) => u.department.id === selectedDeptId) ?? null,
    [units, selectedDeptId],
  );

  const handleToggleExpand = useCallback((deptId: string) => {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return next;
    });
  }, []);

  const handleSelectDept = useCallback((deptId: string) => {
    setSelectedDeptId(deptId);
    setSheetOpen(true);
  }, []);

  const handleCloseSheet = useCallback(() => {
    setSheetOpen(false);
    setSelectedDeptId(null);
  }, []);

  const isLoadingKpis = loadingHoshin || loadingProjects;

  return (
    <ModuleLayout>
      <div className="flex flex-col h-full overflow-hidden bg-[var(--bg-app)] text-[var(--text-primary)]">
        {/* ── Executive Ribbon ── */}
        <ExecRibbon
          hoshinCount={hoshinCount}
          projectsOnTime={projectsOnTime}
          andonTriggered={andonCounts.andon}
          critical={andonCounts.critical}
          isLoadingKpis={isLoadingKpis}
        />

        {/* ── Main matrix grid ── */}
        <div className="flex-1 overflow-auto p-3">
          {/* Fallback notice banner */}
          {isFallback && !loadingUnits && (
            <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-[3px] border border-amber-400/30 bg-amber-500/5 text-[10px] text-amber-700 dark:text-amber-400">
              <AlertTriangle size={11} className="flex-shrink-0" />
              <span>
                Nenhum departamento configurado — exibindo estrutura padrão de macro-setores.
                Configure seus departamentos em <strong>Administração → Pessoas → Departamentos</strong>.
              </span>
            </div>
          )}

          <div className={PANEL}>
            <div className={PANEL_HEADER}>
              <div className="flex items-center gap-2">
                <Users size={11} className="text-[var(--text-secondary)]" />
                <span className={PANEL_TITLE}>Unidades de Valor / Departamentos</span>
                {isFallback && (
                  <span className="text-[9px] font-medium text-amber-600 bg-amber-500/10 border border-amber-400/20 px-1.5 py-0.5 rounded-sm">
                    Estrutura padrão
                  </span>
                )}
              </div>
              <span className="text-[10px] text-[var(--text-muted)]">
                {loadingUnits ? 'Carregando…' : `${units.length} setor${units.length !== 1 ? 'es' : ''}`}
              </span>
            </div>

            {loadingUnits ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-[11px] text-[var(--text-muted)] animate-pulse">Carregando estrutura organizacional…</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[var(--bg-elevated)]">
                      <th className="w-6 px-2 py-1.5" />
                      <th className="px-2 py-1.5 text-left text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Setor</th>
                      <th className="px-2 py-1.5 text-left text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Gestor</th>
                      <th className="px-2 py-1.5 text-center text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)] w-16">Time</th>
                      <th className="px-2 py-1.5 text-center text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)] w-16">Mapas</th>
                      <th className="px-2 py-1.5 text-center text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)] w-16">Projetos</th>
                      <th className="px-2 py-1.5 text-left text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)] w-24">FTE</th>
                      <th className="px-2 py-1.5 text-center text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)] w-16">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-default)]">
                    {units.map((unit) => (
                      <DeptRow
                        key={unit.department.id}
                        unit={unit}
                        orgId={orgId}
                        isSelected={selectedDeptId === unit.department.id}
                        isExpanded={expandedDepts.has(unit.department.id)}
                        onToggleExpand={handleToggleExpand}
                        onSelect={handleSelectDept}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Hint when no dept is selected */}
          {!sheetOpen && units.length > 0 && (
            <div className="flex items-center justify-center gap-1.5 mt-3 text-[10px] text-[var(--text-muted)] select-none">
              <PanelBottomOpen size={11} />
              Clique em um setor para abrir a Central de Ações
            </div>
          )}
        </div>

        {/* ── Bottom Sheet — Central de Ações ── */}
        {sheetOpen && selectedDeptId && (
          <ActionSheet
            orgId={orgId}
            deptId={selectedDeptId}
            deptName={selectedUnit?.department.name ?? '—'}
            isSynthetic={selectedUnit?.isSynthetic ?? false}
            onClose={handleCloseSheet}
          />
        )}
      </div>
    </ModuleLayout>
  );
};

export default CockpitDiretoriaPage;

